/**
 * SollDeckungTab — the Regelwerk Soll-Deckungskatalog (P2 Placement Slice 3).
 *
 * Filter by Ebene + Sparte; a table of the catalog rows (Sparte / Regel / Pflicht-Status / Zielsumme
 * / Rechtsgrundlage / Prio / Herkunft / Aktiv); per-row Bearbeiten / Zuruecksetzen / Deaktivieren and
 * a «Neue Regel». All write controls are gated on `canManage` (regelwerk.manage); the api is the hard line.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IGB2B_CODE_LABELS,
  SOLL_PFLICHT_STATUS_LABELS,
  igb2bCodeLabel,
  type RegelwerkSollDeckungItem,
} from '@brokly/shared';
import { apiClient } from '../../../../lib/api-client';
import { useRegelwerkSollDeckung } from '../../hooks';
import { Button, LoadingState, ErrorState, EmptyState } from '../../components';
import { RuleEditDialog } from './RuleEditDialog';

export function SollDeckungTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [ebene, setEbene] = useState<string>('');
  const [branchencode, setBranchencode] = useState<string>('');
  const filters = useMemo(
    () => ({ ebene: ebene || undefined, branchencode: branchencode || undefined, include_inactive: true }),
    [ebene, branchencode],
  );
  const { data, isLoading, isError } = useRegelwerkSollDeckung(filters);

  const [editing, setEditing] = useState<RegelwerkSollDeckungItem | null>(null);
  const [creating, setCreating] = useState(false);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin', 'regelwerk', 'soll-deckung'] });

  const deactivate = useMutation({
    mutationFn: (id: string) => apiClient.admin.patchRegelwerkSollRule(id, { aktiv: false }),
    onSuccess: invalidate,
  });
  const reset = useMutation({
    mutationFn: (id: string) => apiClient.admin.resetRegelwerkSollRule(id),
    onSuccess: invalidate,
  });

  // The Sparte filter options = the branchencodes actually present in the catalog.
  const codeOptions = useMemo(() => {
    const codes = new Set((data?.items ?? []).map((i) => i.branchencode));
    return [...codes].sort();
  }, [data]);

  return (
    <div data-testid="regelwerk-soll-tab" className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Ebene
          <select
            value={ebene}
            onChange={(e) => setEbene(e.target.value)}
            aria-label="Ebene filtern"
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          >
            <option value="">Alle</option>
            <option value="police">Police</option>
            <option value="kunde">Kunde</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Sparte
          <select
            value={branchencode}
            onChange={(e) => setBranchencode(e.target.value)}
            aria-label="Sparte filtern"
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          >
            <option value="">Alle</option>
            {codeOptions.map((c) => (
              <option key={c} value={c}>
                {c} {IGB2B_CODE_LABELS[c] ?? ''}
              </option>
            ))}
          </select>
        </label>
        <div className="ml-auto">
          {canManage && (
            <Button size="sm" onClick={() => setCreating(true)} data-testid="regelwerk-neue-regel">
              Neue Regel
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : isError || data == null ? (
        <ErrorState />
      ) : data.items.length === 0 ? (
        <EmptyState label="Keine Regeln fuer diese Filter." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Sparte</th>
                <th className="px-3 py-2">Regel</th>
                <th className="px-3 py-2">Pflicht-Status</th>
                <th className="px-3 py-2">Zielsumme</th>
                <th className="px-3 py-2">Rechtsgrundlage</th>
                <th className="px-3 py-2">Prio</th>
                <th className="px-3 py-2">Herkunft</th>
                <th className="px-3 py-2">Aktiv</th>
                {canManage && <th className="px-3 py-2">Aktionen</th>}
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.id} data-testid={`regelwerk-row-${item.rule_key}-${item.branchencode}`} className="border-t border-border">
                  <td className="px-3 py-2 tabular-nums">{item.branchencode} {igb2bCodeLabel(item.branchencode)}</td>
                  <td className="px-3 py-2 font-medium">{item.label}</td>
                  <td className="px-3 py-2">{SOLL_PFLICHT_STATUS_LABELS[item.pflicht_status]}</td>
                  <td className="px-3 py-2 tabular-nums">{item.ziel_deckungssumme ?? '-'}</td>
                  <td className="px-3 py-2">{item.rechtsgrundlage ?? '-'}</td>
                  <td className="px-3 py-2 tabular-nums">{item.prioritaet}</td>
                  <td className="px-3 py-2">{item.origin === 'system' ? 'Standard' : 'Eigen'}</td>
                  <td className="px-3 py-2">{item.aktiv ? 'Ja' : 'Nein'}</td>
                  {canManage && (
                    <td className="px-3 py-2">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditing(item)}>
                          Bearbeiten
                        </Button>
                        {item.origin === 'system' && (
                          <Button size="sm" variant="ghost" disabled={reset.isPending} onClick={() => reset.mutate(item.id)}>
                            Zuruecksetzen
                          </Button>
                        )}
                        {item.aktiv && (
                          <Button size="sm" variant="ghost" disabled={deactivate.isPending} onClick={() => deactivate.mutate(item.id)}>
                            Deaktivieren
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <RuleEditDialog mode="edit" rule={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); invalidate(); }} />
      )}
      {creating && (
        <RuleEditDialog mode="create" onClose={() => setCreating(false)} onSaved={() => { setCreating(false); invalidate(); }} />
      )}
    </div>
  );
}
